import { useCampaignSetting } from '@campaign-services/hooks/use-campaign-setting'
import { useCampaignCode } from '@campaign-services/hooks/useCampaignCode'
import { useFpCampaignCartSelector } from '@cart-services/hooks/useFpCampaignCartStore'
import type { CountryCode, ProjectCode } from '@constants'
import { useFastpassCampaignGoogleAnalyticEvent } from '@dni-analysis'
import { useApiCampaignActivityInfo } from '@hooks-api/fortune/useApiCampaignActivityInfo'
import { HStack } from '@pets-ui/HStack/HStack'
import { Image } from '@pets-ui/Image/Image'
import { Text } from '@pets-ui/Text/Text'
import * as stylex from '@stylexjs/stylex'
import { useState } from 'react'
import { Link } from 'react-router-dom-v5'
import { useCampaignBrands } from '../hooks/use-campaign-brands'
import { resolveBrandIcon } from '../utils/resolve-brand-icon'
import { AppBar } from './AppBar/AppBar'
import { AppBarSearch } from './AppBar/AppBarSearch'
import { useAppBar } from './AppBar/useAppBar'
import { AppBarAccount } from './AppBarAccount/AppBarAccount'
import { AppBarAdminScan } from './AppBarAdminScan/AppBarAdminScan'
import { AppBarCart } from './AppBarCart/AppBarCart'
import { Searcher } from './LegacySearcher/Searcher/Searcher'
const styles = stylex.create({
  root: {
    padding: 12,
  },
})

interface LeftHeaderProps {
  logoHref?: string
}

export const LeftHeader = ({ logoHref }: LeftHeaderProps) => {
  const { campaignCodeFromUrl } = useCampaignCode()
  const { destructuredData: campaign } = useApiCampaignActivityInfo(campaignCodeFromUrl)
  const { brands, projectCode } = useCampaignBrands()
  const brandInfo = brands.find(brand => brand.project_code === projectCode)

  const brandIcon = resolveBrandIcon(campaign, projectCode) ?? brandInfo?.brand_image

  return (
    <HStack gap={2} alignItems="center" flexWrap="nowrap" minWidth={0}>
      <Link to={logoHref ?? `/campaigns/${campaignCodeFromUrl}`}>
        <Image src={brandIcon} alt={brandInfo?.brand_name} width={80} height={32} />
      </Link>
      <Text variant="body" lineClamp={2}>{campaign?.title}</Text>
    </HStack>
  )
}

const RightHeader = ({ projectCode, countryCode, campaignId }: { projectCode: ProjectCode, countryCode: CountryCode, campaignId: number }) => {
  const { isMemberLoginTypeGuest, isCampaignClosed } = useCampaignSetting()
  const { items } = useFpCampaignCartSelector(campaignId ?? null, state => state.cart)
  const totalQuantity = items.reduce((acc, item) => acc + item.quantity, 0)
  const { isCartDisabled, toCartPage, onLogout } = useAppBar()
  const [isOpen, setIsOpen] = useState(false)

  const {
    sendBeginCartEvent,
    sendClickSearchBarEvent,
  } = useFastpassCampaignGoogleAnalyticEvent({ projectCode, countryCode, campaignId })

  const handleCartClick = () => {
    sendBeginCartEvent()
    toCartPage?.()
  }

  const handleDrawerToggle = () => {
    sendClickSearchBarEvent()
    setIsOpen(!isOpen)
  }

  return (
    <>
      <HStack flexWrap="nowrap">
        {!isMemberLoginTypeGuest && <AppBarAdminScan />}
        {!isMemberLoginTypeGuest && <AppBarAccount onLogoutClick={onLogout} />}
        {!isCampaignClosed && <AppBarCart isCartDisabled={isCartDisabled} onClick={handleCartClick} cartItemQuantity={totalQuantity} />}
        {!isCampaignClosed && <AppBarSearch onClick={() => setIsOpen(true)} />}
      </HStack>
      {
        projectCode && campaignId
        && <Searcher campaignId={campaignId} isDrawerOpen={isOpen} onDrawerToggle={handleDrawerToggle} projectCode={projectCode} countryCode={countryCode} environment="campaign" />
      }
    </>
  )
}

export const TheCampaignHeader = ({ projectCode, countryCode, campaignId }: { projectCode: ProjectCode, countryCode: CountryCode, campaignId: number }) => {
  return (
    <AppBar
      left={<LeftHeader />}
      right={projectCode && countryCode && campaignId ? <RightHeader projectCode={projectCode} countryCode={countryCode} campaignId={campaignId} /> : null}
      rootStyle={styles.root}
    />
  )
}
